import { Loader2 } from "lucide-react";
import { STATUS } from "../lib/format";

export const Facet = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M6 3h12l4 6-10 12L2 9z" />
    <path d="M2 9h20M12 21 8 9l4-6 4 6z" />
  </svg>
);

export const StatusBadge = ({ status, testid, large }) => {
  if (!status) return <span className="text-slate-400 text-sm" data-testid={testid}>—</span>;
  const s = STATUS[status];
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-full border ${s.border} ${s.bg} ${s.text} font-semibold ${large ? "px-4 py-1.5 text-sm" : "px-2.5 py-0.5 text-xs"}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
};

export const StatusDot = ({ status, testid }) => (
  <span
    data-testid={testid}
    title={status ? STATUS[status].label : "No target"}
    className={`inline-block w-2.5 h-2.5 rounded-full ${status ? STATUS[status].dot : "bg-slate-300"}`}
  />
);

export const Eyebrow = ({ children, className = "" }) => <div className={`bs-eyebrow ${className}`}>{children}</div>;

export const PageHeader = ({ eyebrow, title, right, testid }) => (
  <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 bs-rise">
    <div>
      {eyebrow && <Eyebrow className="flex items-center gap-2 text-[#C5A059]"><Facet className="w-3.5 h-3.5" />{eyebrow}</Eyebrow>}
      <h1 data-testid={testid} className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#011E38] mt-2">
        {title}
      </h1>
    </div>
    {right}
  </div>
);

export const StatCard = ({ label, value, sub, testid, status, accent }) => (
  <div className={`bs-card p-6 bs-rise ${accent ? "bs-navy-panel border-0" : ""}`} data-testid={testid}>
    <div className="flex items-start justify-between gap-2">
      <Eyebrow className={accent ? "!text-white/60" : ""}>{label}</Eyebrow>
      {status !== undefined && <StatusBadge status={status} />}
    </div>
    <div className={`num text-3xl sm:text-4xl font-bold mt-3 ${accent ? "text-white" : "text-[#011E38]"}`} data-testid={testid && `${testid}-value`}>
      {value}
    </div>
    {sub && <div className={`text-sm mt-1.5 ${accent ? "text-white/60" : "text-slate-500"}`}>{sub}</div>}
  </div>
);

export const KpiTile = ({ label, value, status, testid, sub }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={testid}>
    <div className="flex items-center justify-between">
      <span className="text-[0.7rem] uppercase tracking-[0.14em] font-semibold text-slate-500">{label}</span>
      {status !== undefined && <StatusDot status={status} />}
    </div>
    <div className="num text-2xl font-bold text-[#011E38] mt-1.5" data-testid={testid && `${testid}-value`}>{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
  </div>
);

export const BasisToggle = ({ basis, setBasis, dark }) => (
  <div className={`inline-flex rounded-full p-1 text-xs font-semibold ${dark ? "bg-white/10" : "bg-slate-100"}`} data-testid="basis-toggle">
    {[["total", "Total Sales"], ["ex_coin", "Without Gold Coin"]].map(([v, l]) => (
      <button key={v} type="button" onClick={() => setBasis(v)} data-testid={`basis-${v}`}
        className={`px-3 py-1.5 rounded-full whitespace-nowrap transition-colors ${basis === v ? (dark ? "bg-white text-[#011E38]" : "bg-[#011E38] text-white") : dark ? "text-white/70" : "text-slate-500"}`}>
        {l}
      </button>
    ))}
  </div>
);

export const Loading = ({ label = "Loading" }) => (
  <div className="flex items-center justify-center py-24 text-slate-500 gap-3" data-testid="loading-indicator">
    <Loader2 className="w-5 h-5 animate-spin text-[#011E38]" /> {label}…
  </div>
);

export const Empty = ({ children }) => (
  <div className="text-center text-slate-500 py-12 text-sm">{children}</div>
);

export const SectionTitle = ({ eyebrow, title, right }) => (
  <div className="flex items-end justify-between gap-3 mb-4">
    <div>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h2 className="font-display text-xl sm:text-2xl font-semibold text-[#011E38] tracking-tight mt-1">{title}</h2>
    </div>
    {right}
  </div>
);
